import React, { useState } from 'react';
import { X, Loader2, Heart } from 'lucide-react';
import { useGuestIdentity } from '../utils/useGuestIdentity';

/**
 * Tiny email-only capture modal triggered by the first heart-click of an
 * un-registered visitor. One field, two buttons. No spam. Never re-prompts.
 */
const InspoEmailPrompt = ({ isOpen, onClose, onSuccess, onSkip }) => {
  const { register } = useGuestIdentity();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Please enter your email.'); return; }
    setBusy(true);
    try {
      const data = await register(email.trim(), ''); // name auto-derived from email local-part
      onSuccess && onSuccess(data);
    } catch (err) {
      setError(err.message || 'Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center px-3 sm:px-5 bg-black/55 backdrop-blur-sm"
      data-testid="inspo-email-prompt"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-sm bg-white text-black border border-black/5 shadow-2xl">
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <Heart size={14} className="text-red-500" fill="currentColor" />
            <p className="text-[10px] tracking-[0.25em] uppercase text-black/40">Saved to your favourites</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-black/40 hover:text-black tap-target" data-testid="inspo-email-prompt-close">
            <X size={16} />
          </button>
        </div>
        <div className="px-5 pb-5">
          <h2 className="font-presti text-lg leading-tight mb-1.5">Sign in to create a shareable inspo list</h2>
          <p className="text-black/55 text-xs mb-4 leading-relaxed">
            Drop your email to save your favourites across sessions. No password, no spam.
          </p>
          <form onSubmit={submit} className="space-y-3">
            <input
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-0 py-2.5 bg-transparent border-b border-black/20 focus:border-black outline-none text-sm"
              placeholder="you@email.com"
              data-testid="inspo-email-prompt-input"
              required
              autoFocus
            />
            {error && <p className="text-red-500 text-xs" data-testid="inspo-email-prompt-error">{error}</p>}
            <div className="flex items-center gap-3 pt-1">
              <button
                type="submit"
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-black text-white text-[11px] tracking-[0.2em] uppercase font-medium hover:bg-black/80 transition-colors disabled:bg-black/40"
                data-testid="inspo-email-prompt-submit"
              >
                {busy ? <><Loader2 size={12} className="animate-spin" /> Saving...</> : <>Sign me in</>}
              </button>
              <button
                type="button"
                onClick={onSkip || onClose}
                className="text-[11px] tracking-[0.2em] uppercase text-black/40 hover:text-black px-2"
                data-testid="inspo-email-prompt-skip"
              >
                Maybe later
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InspoEmailPrompt;
